import { useRouterState } from "@tanstack/react-router"
import { ChevronRight, ShieldCheck } from "lucide-react"
import { Fragment, type Ref, useEffect, useRef, useState } from "react"

import PayApplePay from "@/assets/img/compatibility/checkout/pay-applepay.svg"
import PayMaestro from "@/assets/img/compatibility/checkout/pay-maestro.svg"
import PayMastercard from "@/assets/img/compatibility/checkout/pay-mastercard.svg"
import PayVisa from "@/assets/img/compatibility/checkout/pay-visa.svg"
import VerifiedIcon from "@/assets/img/checkout/verified.svg"
import AdvEnergy from "@/assets/img/aura/checkout/adv-energy.webp"
import AdvGrowth from "@/assets/img/aura/checkout/adv-growth.webp"
import AdvSensitivity from "@/assets/img/aura/checkout/adv-sensitivity.webp"
import AdvSignature from "@/assets/img/aura/checkout/adv-signature.webp"
import AdvStrengths from "@/assets/img/aura/checkout/adv-strengths.webp"
import GuaranteeShield from "@/assets/img/aura/checkout/guarantee-shield.svg"
import Hero from "@/assets/img/aura/checkout/hero.webp"
import HeroBg from "@/assets/img/aura/checkout/hero-bg.webp"
import Payment2Hero from "@/assets/img/aura/checkout/payment2-hero.webp"
import UserEmily from "@/assets/img/aura/checkout/user-emily.webp"
import UserHaley from "@/assets/img/aura/checkout/user-haley.webp"
import UserSophia from "@/assets/img/aura/checkout/user-sophia.webp"
import StarGold from "@/assets/img/soulmate/star-gold.svg"
import { RatingStars } from "@/features/advisors"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { hasAdditionalOffers } from "@/features/offers"
import { usePlanSelection } from "@/features/products"
import type { ProductPlan } from "@/features/products/types"
import { cn } from "@/lib/utils"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { isValidEmail } from "@/shared/lib/email"
import { formatMoney } from "@/shared/lib/money"
import { AppRoutes } from "@/shared/lib/routes"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { LegalLink } from "@/shared/components/legal-link"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useQuizStore } from "@/stores/quiz-store"
import { buildConsentDisclaimer } from "../domain/disclaimer"
import { savingsPercent, strikePrice } from "../domain/pricing"
import { isApplePayAvailable, useCheckout } from "../hooks/use-checkout"
import { ApplePaySheet } from "./apple-pay-sheet"
import { CardPaymentSheet } from "./card-payment-sheet"
import { MoneyBackGuarantee } from "./money-back-guarantee"
import { PaymentButtons } from "./payment-buttons"

type OpenSheet = "card" | "applepay" | null

const ADVANTAGES = [
  {
    icon: AdvSignature,
    title: "Aura Signature Profile",
    text: "See your aura colors, archetype, and the energy that shapes your unique presence",
  },
  {
    icon: AdvSensitivity,
    title: "Sensitivity Profile",
    text: "Learn how strongly you feel, absorb, and respond to emotional energy around you",
  },
  {
    icon: AdvStrengths,
    title: "Natural Strengths",
    text: "Discover the natural abilities and inner strengths that make your energy unique",
  },
  {
    icon: AdvGrowth,
    title: "Growth Edges",
    text: "See where your mind and natural sensitivity may become overwhelming",
  },
  {
    icon: AdvEnergy,
    title: "Energy & Connection Patterns",
    text: "Understand how your energy flows through your connections and interactions with others",
  },
]

const REVIEWS = [
  {
    avatar: UserEmily,
    name: "Emily Heaton",
    rating: 4.8,
    text: "I always knew I was sensitive to other people's energy, but never realized how much I absorbed. The reading helped me understand when I need to step back.",
  },
  {
    avatar: UserHaley,
    name: "Haley Grace",
    rating: 4.8,
    text: "Seeing my strengths and growth edges felt surprisingly accurate. It explained why some people energize me while others leave me completely drained.",
  },
  {
    avatar: UserSophia,
    name: "Sophia Mitchell",
    rating: 4.9,
    text: "I didn't expect my aura profile to feel this personal. The part about how I connect with people was almost scary accurate and made me notice patterns I'd never thought about before.",
  },
]

/** mm:sec countdown shown in the navbar — two boxed digits with a dotted divider. */
function Countdown({ seconds }: { seconds: number }) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0")
  const ss = String(seconds % 60).padStart(2, "0")
  const box = (value: string, label: string) => (
    <div className="flex h-10 w-12 flex-col items-center justify-center rounded-lg bg-purple-light/10">
      <span className="-mb-1 font-display text-xl font-medium text-purple-light tabular-nums">
        {value}
      </span>
      <span className="text-xs text-purple-light/40">{label}</span>
    </div>
  )
  return (
    <div className="flex items-center gap-1">
      {box(mm, "min")}
      <div className="flex flex-col gap-0.5">
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
      </div>
      {box(ss, "sec")}
    </div>
  )
}

/** "Total due today: ——— $42.99 › $1.00 / N% SAVED" row, from live plan data. */
function PriceRow({ plan }: { plan: ProductPlan }) {
  const strike = strikePrice(plan)
  const savings = savingsPercent(plan)
  return (
    <div className="flex items-center gap-4">
      <span className="text-base font-semibold text-purple-light">Total due today:</span>
      <div className="h-px flex-1 bg-purple-light/20" />
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-1">
          {strike != null && (
            <span className="text-base text-purple-light/40 line-through decoration-peach">
              {formatMoney(strike, plan.currency)}
            </span>
          )}
          <ChevronRight className="size-3 text-purple-light/40" />
          <span className="text-base font-semibold text-white">
            {formatMoney(plan.price, plan.currency)}
          </span>
        </div>
        {savings > 0 && (
          <span className="rounded-full bg-green/20 px-2 py-0.5 text-[10px] font-semibold text-green">
            {savings}% SAVED
          </span>
        )}
      </div>
    </div>
  )
}

/** Secure-payment + money-back card shown under the primary pay block. */
function SecurePaymentCard({ plan }: { plan: ProductPlan }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-3xl border border-white/10 p-4">
      <p className="text-sm font-semibold text-white">Safe &amp; Secure Payment</p>
      <div className="flex w-full gap-1">
        {[
          { src: PayVisa, cls: "h-3 w-[37px]" },
          { src: PayMastercard, cls: "h-6 w-[31px]" },
          { src: PayMaestro, cls: "h-6 w-[31px]" },
          { src: PayApplePay, cls: "h-4 w-[38px]" },
        ].map((p) => (
          <div
            key={p.src}
            className="flex h-10 flex-1 items-center justify-center rounded-lg bg-white/10"
          >
            <img src={p.src} alt="" className={p.cls} />
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-white/10" />
      <MoneyBackGuarantee />
      <p className="text-center text-xs text-purple-light/40">{buildConsentDisclaimer(plan)}</p>
    </div>
  )
}

/**
 * The Aura Scan checkout — the `aura-scan` variant (Figma LPR_Quiz_Aura_Subscription).
 * Prices/disclaimers come from the live plan; pay buttons reuse the shared primitives.
 */
export function AuraCheckout() {
  const navigate = useNavigateWithParams()
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const purchaseStatus = usePurchaseStore((s) => s.status)
  const email = (useQuizStore((s) => s.answers.email) as string) || ""
  const { selectedPlan, isLoading } = usePlanSelection()

  const [sheet, setSheet] = useState<OpenSheet>(null)
  const [applePayAvailable] = useState(isApplePayAvailable)
  const { payWithApplePay } = useCheckout(selectedPlan ?? undefined, email, { initCard: false })

  const paymentRef = useRef<HTMLDivElement>(null)
  const scrollToPayment = () =>
    paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  const [seconds, setSeconds] = useState(9 * 60 + 45)
  const [paymentOnScreen, setPaymentOnScreen] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const el = paymentRef.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => setPaymentOnScreen(entry.isIntersecting), {
      rootMargin: "0px 0px -40% 0px",
    })
    io.observe(el)
    return () => io.disconnect()
  }, [selectedPlan?.id])

  const nextRoute = hasAdditionalOffers()
    ? AppRoutes.ADDITIONAL_OFFERS
    : typeof search.upsell === "string" && search.upsell
      ? AppRoutes.UPSELL
      : AppRoutes.CREATE_ACCOUNT

  useEffect(() => {
    if (!selectedPlan) return
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: selectedPlan.id,
      price: selectedPlan.price,
      currency: selectedPlan.currency,
    })
    trackEvent(ANALYTICS_EVENTS.AURA_SUB_SCREEN_SHOWN)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id])

  useEffect(() => {
    if (purchaseStatus === "success") navigate({ to: nextRoute })
  }, [purchaseStatus, navigate, nextRoute])

  const handleSuccess = () => {
    setSheet(null)
    navigate({ to: nextRoute })
  }

  const handleApplePay = async () => {
    trackEvent(ANALYTICS_EVENTS.AURA_SUB_SCREEN_SUB_BUTTON_CLICK, {
      metadata: { method: "apple" },
    })
    if (isValidEmail(email)) {
      const ok = await payWithApplePay()
      if (ok) handleSuccess()
    } else {
      setSheet("applepay")
    }
  }

  if (isLoading || !selectedPlan) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-dark-base">
        <LoadingSpinner />
      </div>
    )
  }

  const renderPay = (ref?: Ref<HTMLDivElement>) => (
    <div className="space-y-4">
      <PriceRow plan={selectedPlan} />
      <div ref={ref}>
        <PaymentButtons
          applePayAvailable={applePayAvailable}
          onApplePay={handleApplePay}
          onCard={() => {
            trackEvent(ANALYTICS_EVENTS.AURA_SUB_SCREEN_SUB_BUTTON_CLICK, {
              metadata: { method: "card" },
            })
            setSheet("card")
          }}
        />
      </div>
    </div>
  )

  return (
    <div className="min-h-[100dvh] bg-dark-base font-body text-purple-light">
      <div
        className={cn(
          "sticky top-0 z-20 border-purple-light/10 border-b bg-dark-base/90 pt-safe backdrop-blur transition-all duration-200",
          paymentOnScreen && "pointer-events-none -translate-y-full opacity-0",
        )}
      >
        <MobileLayout className="flex items-center justify-between py-2">
          <Countdown seconds={seconds} />
          <button
            type="button"
            onClick={scrollToPayment}
            className="flex h-10 w-[180px] items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            Get my results
          </button>
        </MobileLayout>
      </div>

      <MobileLayout className="relative isolate flex flex-col pt-10 pb-safe">
        <img
          src={HeroBg}
          alt=""
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[240px] w-full object-cover"
        />
        <div className="mx-auto w-fit rounded-full bg-green/20 px-3 py-2 text-center text-[10px] font-medium uppercase tracking-wide text-green backdrop-blur">
          Your aura prediction is ready
        </div>
        <h1 className="mt-2 text-center font-display text-2xl font-medium text-purple-light">
          Reveal Your True <span className="text-main-purple">Aura!</span>
        </h1>
        <p className="mt-2 text-center text-base text-purple-light/70">
          Uncover how your energy reflects your true self, strengths, and connections with the
          world.
        </p>

        <div className="relative mx-auto mt-4 aspect-[382/300] w-full max-w-[382px] overflow-hidden">
          <img src={Hero} alt="" className="size-full object-cover object-top" />
        </div>

        <button
          type="button"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.AURA_SUB_SCREEN_BUTTON_CLICK)
            scrollToPayment()
          }}
          className="mt-4 h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
        >
          Get My Prediction
        </button>
        <div className="mt-2 flex items-center justify-center gap-0.5 text-xs text-purple-light/40">
          <ShieldCheck className="size-[15px]" />
          We do not collect your biometric data
        </div>

        <h2 className="mt-10 text-center font-display text-2xl font-medium text-purple-light">
          See what you can uncover and enjoy inside
        </h2>
        <div className="mt-4 flex flex-col gap-2 rounded-[20px] bg-dark-surface p-4">
          {ADVANTAGES.map((a, i) => (
            <Fragment key={a.title}>
              {i > 0 && <div className="h-px w-full bg-purple-light/10" />}
              <div className="flex items-center gap-2.5">
                <img src={a.icon} alt="" className="size-[52px] shrink-0 rounded-[26px]" />
                <div className="min-w-0">
                  <p className="text-base font-semibold text-white">{a.title}</p>
                  <p className="text-sm text-purple-light/70">{a.text}</p>
                </div>
              </div>
            </Fragment>
          ))}
        </div>

        <div className="mt-4">{renderPay(paymentRef)}</div>
        <div className="mt-4">
          <SecurePaymentCard plan={selectedPlan} />
        </div>

        <div className="-mx-4 mt-10 flex flex-col gap-6 bg-dark-surface py-10">
          <h2 className="px-4 font-display text-[32px] font-medium leading-tight text-purple-light">
            What people discovered through their Aura Reading
          </h2>
          <div className="flex items-center gap-2 px-4">
            <img src={StarGold} alt="" className="size-8" />
            <span className="font-display text-2xl font-medium">
              <span className="text-gold">4.8</span>
              <span className="text-purple-light"> (2545 readings)</span>
            </span>
          </div>
          <div className="no-scrollbar overflow-x-auto">
            <div className="flex w-max gap-4 px-4 pb-2">
              {REVIEWS.map((r) => (
                <div
                  key={r.name}
                  className="flex w-[300px] shrink-0 flex-col justify-between gap-6 rounded-2xl border border-purple-light/20 p-4"
                >
                  <p className="text-base text-purple-light/70">{r.text}</p>
                  <div className="flex items-center gap-2">
                    <img src={r.avatar} alt="" className="size-11 rounded-full object-cover" />
                    <div className="flex flex-col gap-1">
                      <span className="font-display text-base font-medium text-purple-light">
                        {r.name}
                      </span>
                      <div className="flex items-center gap-1">
                        <RatingStars rating={r.rating} className="h-4 w-20 text-gold" />
                        <span className="text-sm font-medium text-gold">{r.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="px-4 text-sm">
            <span className="font-medium text-white">*Disclaimer: </span>
            <span className="text-purple-light/70">
              Every aura reading is unique and based on your personal answers and energy profile.
            </span>
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center">
          <img
            src={Payment2Hero}
            alt=""
            className="relative z-0 -mx-4 mb-[-80px] w-[calc(100%+2rem)] max-w-none object-cover"
          />
          <div className="relative z-10 space-y-2 text-center">
            <h2 className="font-display text-2xl font-medium text-purple-light">
              See Yourself Through Your Energy
            </h2>
            <p className="text-base text-purple-light/70">
              Explore your aura, natural strengths, and the patterns that shape your energy with
              Live Palmistry.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-4 rounded-3xl bg-dark-surface p-5">
          {renderPay()}
          <p className="text-center text-xs text-purple-light/40">
            {buildConsentDisclaimer(selectedPlan)}
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <img src={GuaranteeShield} alt="" className="size-20" />
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Satisfaction Guaranteed
            </h2>
            <p className="text-base text-purple-light/70">
              At Live Palmistry, every sketch, compatibility insight, and personal reading is
              designed to feel personal, meaningful, and worth exploring. If your experience
              doesn't feel magical or insightful, we're here for you.
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 rounded-[20px] bg-purple-light/10 px-4 pt-2 pb-3">
            <div className="text-center">
              <p className="font-display text-2xl font-medium text-purple-light">4.8 out of 5</p>
              <p className="text-sm text-purple-light/70">personal readings revealed worldwide</p>
            </div>
            <div className="h-px w-full bg-purple-light/10" />
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <RatingStars rating={4.8} className="h-4 w-20 text-gold" />
                  <span className="text-sm font-medium text-gold">4.8</span>
                </div>
                <p className="text-xs text-purple-light/40">Based on user experiences</p>
              </div>
              <div className="flex items-center gap-1 rounded-2xl bg-green/10 py-1.5 pr-2 pl-1.5">
                <img src={VerifiedIcon} alt="" className="size-4" />
                <span className="text-xs font-semibold text-green">Verified reviews</span>
              </div>
            </div>
          </div>
        </div>

        <footer className="mt-10 flex flex-col gap-2 text-xs text-purple-light/40">
          <p className="text-center">All rights reserved. 2026</p>
          <div className="h-px w-full bg-purple-light/10" />
          <div className="flex text-center">
            <LegalLink to={AppRoutes.PRIVACY} className="flex-1 hover:text-purple-light">
              Privacy Policy
            </LegalLink>
            <LegalLink to={AppRoutes.TERMS} className="flex-1 hover:text-purple-light">
              Terms of use
            </LegalLink>
            <LegalLink to={AppRoutes.COOKIES} className="flex-1 hover:text-purple-light">
              Cookie Policy
            </LegalLink>
          </div>
        </footer>
      </MobileLayout>

      <CardPaymentSheet
        plan={selectedPlan}
        email={email}
        open={sheet === "card"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
      <ApplePaySheet
        plan={selectedPlan}
        email={email}
        open={sheet === "applepay"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
    </div>
  )
}
